import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import NumericCheckbox from "../NumericCheckbox";
import './DropOffModal.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import OfficeCaptureFields from "../OfficeCaptureFields";

const DropOffModal = ({ show, onHide, title, onConfirm, customer, userEmail }) => {
    const { customerId } = useParams(); // Extract customer ID from URL
    const [stocklists, setStocklist] = useState([]);
    const [groupedDescriptions, setGroupedDescriptions] = useState({});
    const [activeGroup, setActiveGroup] = useState(null);
    const [reportID, setReportID] = useState("");
    const [selectedItems, setSelectedItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirmationMessage, setConfirmationMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate(); // Initialize useNavigate for redirection
    const BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const [incidentDate, setIncidentDate] = useState(new Date());
    const [incidentType, setIncidentType] = useState("");
    const [incident, setIncident] = useState("");
    const [photoFile, setPhotoFile] = useState(null);
    const [latitude, setLatitude] = useState(null);
    const [longitude, setLongitude] = useState(null);
    const [demoAppInstalled, setDemoAppInstalled] = useState(null);

    const handleLocationCaptured = (lat, lng) => {
        setLatitude(lat);
        setLongitude(lng);
    };

    const fetchStockList = useCallback(async () => {
        try {
            const response = await fetch(`${BASE_URL}api/stocklist/GetallStock`);
            if (!response.ok) throw new Error("Failed to fetch stock data");
            const data = await response.json();
            setStocklist(data);
            setLoading(false);
        } catch (error) {
            setError(error.message);
            setLoading(false);
        }
    }, [BASE_URL]);

    useEffect(() => {
        fetchStockList();
    }, [fetchStockList]);

    useEffect(() => {
        if (show) {
            setIncidentDate(new Date());
            setPhotoFile(null);
            setLatitude(null);
            setLongitude(null);
            setDemoAppInstalled(customer?.bit1 ?? null);
            setIsSubmitting(false);
        }
    }, [show, customer]);

    const groupDescriptions = useMemo(() => {
        const groupedItems = {};
        stocklists.forEach((item) => {
            const { description, stockGroup } = item;
            if (!groupedItems[stockGroup]) {
                groupedItems[stockGroup] = [{ Description: description, value: 0 }];
            } else {
                groupedItems[stockGroup].push({ Description: description, value: 0 });
            }
        });
        return groupedItems;
    }, [stocklists]);

    useEffect(() => {
        if (Array.isArray(stocklists) && stocklists.length > 0) {
            setGroupedDescriptions(groupDescriptions);
        }
    }, [stocklists, groupDescriptions]);

    const handleValueChange = useCallback((group, descriptionIndex, newValue) => {
        const updatedGroupedDescriptions = { ...groupedDescriptions };
        updatedGroupedDescriptions[group][descriptionIndex].value = newValue;
        setGroupedDescriptions(updatedGroupedDescriptions);

        const selectedDescription = updatedGroupedDescriptions[group][descriptionIndex];
        const selectedStock = stocklists.find(stock => stock.description === selectedDescription.Description);
        const selectedItem = {
            Description: selectedDescription.Description,
            stockListId: selectedStock ? selectedStock.stockListId : undefined,
            quantity: newValue
        };

        const index = selectedItems.findIndex(item => item.Description === selectedItem.Description);

        if (index !== -1) {
            let updatedSelectedItems = [...selectedItems];
            if (newValue > 0) {
                updatedSelectedItems[index].quantity = newValue;
            } else {
                // Remove item if quantity is 0
                updatedSelectedItems.splice(index, 1);
            }
            setSelectedItems(updatedSelectedItems);
        } else {
            if (newValue > 0) {
                setSelectedItems(prevItems => [...prevItems, selectedItem]);
            }
        }
    }, [groupedDescriptions, stocklists, selectedItems]);

    const toggleGroup = useCallback((group) => {
        setActiveGroup(activeGroup === group ? null : group);
    }, [activeGroup]);

    // Update: Only require at least one item with quantity > 0
    const isFormValid = () => selectedItems.some(item => item.quantity > 0);

    const handleConfirm = () => {
        if (!isFormValid()) {
            setError("Please select at least one item with a quantity greater than 0.");
            return;
        }
        if (!incidentType) {
            setError("Please select an incident type.");
            return;
        }

        // Validate incidentDate
        let validIncidentDate = incidentDate instanceof Date && !isNaN(incidentDate)
            ? incidentDate
            : new Date();

        const stockData = selectedItems.map(item => [item.stockListId, item.quantity]);

        if (stockData.length > 0) {
            submitDropOffData(stockData, validIncidentDate);
        }
    };

    const submitDropOffData = (stockData, validIncidentDate) => {
        setError(null);
        setIsSubmitting(true);
        const formData = new FormData();
        // Backend model binding can't populate int[][] stockData from
        // multipart form fields, so it's sent as a JSON string instead
        // (see DropOffViewModel.StockDataJson / ParseStockData server-side).
        formData.append('StockDataJson', JSON.stringify(stockData));
        formData.append('incidentDate', validIncidentDate.toISOString());
        formData.append('incidentType', incidentType);
        formData.append('incident', incident || '');
        formData.append('email', userEmail || '');
        if (latitude != null) formData.append('Latitude', latitude);
        if (longitude != null) formData.append('Longitude', longitude);
        if (demoAppInstalled != null) formData.append('DemoAppInstalled', demoAppInstalled);
        if (photoFile) formData.append('Photo', photoFile);

        fetch(`${BASE_URL}api/StockTransactions/PostDropOffExistingCustomer?WebCustID=${customerId}`, {
            method: 'POST',
            body: formData
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to submit data to the backend');
                }
                // Assuming backend is sending a plain text (not JSON)
                return response.text(); // Use text() instead of json()
            })
            .then(data => {
                // data will now be the plain text, like "DO202469"
                setIsSubmitting(false);
                setConfirmationMessage(`Transaction completed successfully with Document No: ${data}`);
                setReportID(data);
                onConfirm();

                // Navigate to ReportDetails with the report ID
                navigate(`/report/${data}`);
            })
            .catch(error => {
                setIsSubmitting(false);
                setError(error.message);
            });
    };
    

    const handleClose = () => {
        onHide();
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    const modalStyle = {
        display: show ? "block" : "none",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        overflow: "auto",
    };

    const modalContentStyle = {
        backgroundColor: "#fefefe",
        margin: "15% auto",
        padding: "20px",
        border: "1px solid #888",
        width: "50%",
        borderRadius: "10px",
    };

    return (
        <div style={modalStyle}>
            <div style={modalContentStyle} className="p-4 modal-content-custom">
                <span style={{ float: "right", cursor: "pointer" }} onClick={handleClose}>
                    &times;
                </span>
                <h3 className={`modal-title ${title === "Drop Off" ? "drop-off-title" : "pick-up-title"}`}>
                    {title === "Drop Off" ? "📦 Drop Off" : "🚚 Pick Up"}
                </h3>
                {/* New fields for date, incident type, and incident */}
                <div className="row mb-3">
                    <div className="col-md-4">
                        <label htmlFor="incidentDate" className="form-label">Date:</label>
                        <DatePicker
                            id="incidentDate"
                            selected={incidentDate}
                            onChange={date => setIncidentDate(date)}
                            dateFormat="yyyy-MM-dd"
                            className="form-control"
                            isClearable={false}
                        />
                    </div>
                    <div className="col-md-4">
                        <label htmlFor="incidentType" className="form-label">Incident Type:</label>
                        <select
                            id="incidentType"
                            className="form-select"
                            value={incidentType}
                            onChange={e => setIncidentType(e.target.value)}
                        >
                            <option value="" disabled>Select Incident Type</option>
                            <option value="Visit">Visit</option>
                            <option value="Telephone Call">Telephone Call</option>
                            <option value="Whatsapp">Whatsapp</option>
                            <option value="Email">Email</option>
                        </select>
                    </div>
                    <div className="col-md-4">
                        <label htmlFor="incident" className="form-label">Incident:</label>
                        <textarea
                            id="incident"
                            className="form-control"
                            rows={2}
                            value={incident}
                            onChange={e => setIncident(e.target.value)}
                        />
                    </div>
                </div>
                <OfficeCaptureFields
                    demoAppInstalled={demoAppInstalled}
                    onDemoAppInstalledChange={setDemoAppInstalled}
                    onPhotoSelected={setPhotoFile}
                    onLocationCaptured={handleLocationCaptured}
                    existingPhotoUrl={customer?.vcmx5 ? `${BASE_URL}${customer.vcmx5}` : null}
                    existingLatitude={customer?.flt1}
                    existingLongitude={customer?.flt2}
                />
                <div className="accordion" id="descriptionAccordion">
                    <h3>Select Items</h3>
                    {Object.keys(groupedDescriptions).map((group) => (
                        <div className="accordion-item" key={group}>
                            <h2 className="accordion-header" id={`heading${group}`}>
                                <button
                                    className="accordion-button"
                                    type="button"
                                    onClick={() => toggleGroup(group)}
                                >
                                    <strong>Stock Group: {group}</strong>
                                </button>
                            </h2>
                            {activeGroup === group && (
                                <div
                                    className="accordion-collapse collapse show"
                                    aria-labelledby={`heading${group}`}
                                >
                                    <div className="accordion-body">
                                    <div className="table-responsive">
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th>Description</th>
                                                    <th>Quantity</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {groupedDescriptions[group].map((item, index) => (
                                                    <tr key={index}>
                                                        <td>{item.Description}</td>
                                                        <td>
                                                            <NumericCheckbox
                                                                className="numeric-checkbox"
                                                                value={item.value}
                                                                onValueChange={(newValue) =>
                                                                    handleValueChange(group, index, newValue)
                                                                }
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <div className="d-flex justify-content-end mt-4">
                    <button className="btn btn-primary me-2" onClick={handleConfirm} disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Processing...
                            </>
                        ) : (
                            "Confirm"
                        )}
                    </button>
                    <button className="btn btn-secondary" onClick={handleClose} disabled={isSubmitting}>
                        Cancel
                    </button>
                </div>
                {confirmationMessage && <div className="mt-4 alert alert-success">{confirmationMessage}</div>}
                {error && <div className="mt-4 alert alert-danger">{error}</div>}
            </div>
        </div>
    );
};

export default DropOffModal;
